import {
  Button,
  Checkbox,
  Divider,
  Group,
  isLightColor,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core"
import { TimeInput } from "@mantine/dates"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetRequestableAbsenceShiftTypesForDepartment,
  useGetRequestablePresenceShiftTypesForDepartment,
} from "@/orval/gen/api/shift-type/shift-type"
import { useSelectedDepartmentIds } from "../../-components/useSelectedDepartmentIds"

export type QuickAddShiftSelection = {
  action?: "add" | "delete"
  kind?: "presence" | "absence"
  canDirectAdd?: boolean
  addMode?: "direct" | "modal"
  startTime?: string | null
  endTime?: string | null
  shiftTypeId: number
  name: string
}

function hasValidTimeRange(
  shift: Pick<QuickAddShiftSelection, "startTime" | "endTime">
) {
  return Boolean(
    shift.startTime && shift.endTime && shift.startTime !== shift.endTime
  )
}

function normalizeCssColor(color?: string | null) {
  if (!color) {
    return undefined
  }

  const normalized = color.trim()
  if (!normalized) {
    return undefined
  }

  const toHex2 = (value: number) => value.toString(16).padStart(2, "0")

  const fromArgbInt = (value: number) => {
    const unsigned = value >>> 0
    const alpha = (unsigned >>> 24) & 0xff
    const red = (unsigned >>> 16) & 0xff
    const green = (unsigned >>> 8) & 0xff
    const blue = unsigned & 0xff

    if (alpha === 0) {
      return undefined
    }

    return `#${toHex2(red)}${toHex2(green)}${toHex2(blue)}`
  }

  if (/^-?\d+$/.test(normalized)) {
    const numeric = Number.parseInt(normalized, 10)
    if (Number.isSafeInteger(numeric)) {
      return fromArgbInt(numeric)
    }
  }

  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const rgb = normalized.slice(1)
    return `#${rgb[0]}${rgb[0]}${rgb[1]}${rgb[1]}${rgb[2]}${rgb[2]}`
  }

  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
    return normalized
  }

  if (/^#[0-9a-fA-F]{8}$/.test(normalized)) {
    const alpha = normalized.slice(1, 3)
    if (alpha.toLowerCase() === "00") {
      return undefined
    }

    return `#${normalized.slice(3)}`
  }

  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    return `#${normalized}`
  }

  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `#${normalized}`
  }

  // Backend can send .NET ARGB hex without '#': AARRGGBB.
  // If alpha is 00, treat as transparent (no explicit color).
  // Otherwise convert to CSS hex by dropping alpha: #RRGGBB.
  if (/^[0-9a-fA-F]{8}$/.test(normalized)) {
    const alpha = normalized.slice(0, 2)
    if (alpha.toLowerCase() === "00") {
      return undefined
    }

    return `#${normalized.slice(2)}`
  }

  if (
    /^(rgb|rgba|hsl|hsla)\(/i.test(normalized) ||
    /^var\(/i.test(normalized)
  ) {
    return normalized
  }

  if (/^[a-zA-Z]+$/.test(normalized)) {
    return normalized
  }

  return undefined
}

function getReadableTextColor(
  backColor?: string | null,
  foreColor?: string | null
) {
  const normalizedForeColor = normalizeCssColor(foreColor)
  if (normalizedForeColor) {
    return normalizedForeColor
  }

  const normalizedBackColor = normalizeCssColor(backColor)
  if (!normalizedBackColor) {
    return undefined
  }

  try {
    return isLightColor(normalizedBackColor)
      ? "var(--mantine-color-dark-9)"
      : "var(--mantine-color-white)"
  } catch {
    return "var(--mantine-color-white)"
  }
}

interface QuickAddShiftPanelContainerProps {
  departmentTreeNodes: string[]
}

interface QuickAddShiftPanelInnerProps {
  departmentId: number
  selectedQuickAddShift: QuickAddShiftSelection | null
  setSelectedQuickAddShift: (value: QuickAddShiftSelection | null) => void
  shiftKinds?: Array<"presence" | "absence">
}

export function QuickAddShiftPanel({
  departmentTreeNodes,
  ...props
}: QuickAddShiftPanelContainerProps &
  Omit<QuickAddShiftPanelInnerProps, "departmentId">) {
  const { t } = useTranslation("app_server")
  const selectedDepartmentIds = useSelectedDepartmentIds(departmentTreeNodes)
  const departmentId = selectedDepartmentIds[0]

  const InnerComponent = useMemo(() => {
    if (departmentId === undefined) {
      return t("QuickAdd_SelectDepartment")
    }
    if (selectedDepartmentIds.length > 1) {
      return t("QuickAdd_SelectSingleDepartment")
    }
    return <QuickAddShiftPanelInner departmentId={departmentId} {...props} />
  }, [departmentId, props, selectedDepartmentIds.length, t])

  return (
    <Paper
      withBorder
      p={8}
      h="100%"
      style={{
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Text fw={600} size="sm">
        {props.shiftKinds?.includes("presence") &&
        props.shiftKinds?.includes("absence")
          ? t("QuickAdd_ShiftsAndAbsences")
          : props.shiftKinds?.includes("absence")
            ? t("QuickAdd_Absences")
            : t("QuickAdd_Shifts")}
      </Text>
      {InnerComponent}
    </Paper>
  )
}

function QuickAddShiftPanelInner({
  departmentId,
  selectedQuickAddShift,
  setSelectedQuickAddShift,
  shiftKinds = ["presence", "absence"],
}: QuickAddShiftPanelInnerProps) {
  const showPresence = shiftKinds.includes("presence")
  const showAbsence = shiftKinds.includes("absence")
  const { t } = useTranslation("app_server")

  const shiftTypeQuery = useGetRequestablePresenceShiftTypesForDepartment(
    {
      departmentId,
    },
    { query: { enabled: showPresence } }
  )

  const absenceShiftTypeQuery = useGetRequestableAbsenceShiftTypesForDepartment(
    {
      departmentId,
    },
    {
      query: { enabled: showAbsence },
    }
  )

  const isLoading = shiftTypeQuery.isLoading || absenceShiftTypeQuery.isLoading

  const shiftTypes = useMemo(() => {
    const merged = new Map<
      string,
      {
        kind: "presence" | "absence"
        shiftTypeId: number
        name: string
        backColor?: string | null
        foreColor?: string | null
        canDirectAdd: boolean
        requiresTimeRangeInput: boolean
      }
    >()

    for (const shiftType of shiftTypeQuery.data ?? []) {
      if (shiftType.shiftTypeId === undefined || !shiftType.name) {
        continue
      }
      const key = `presence-${shiftType.shiftTypeId}`
      if (merged.has(key)) {
        continue
      }
      merged.set(key, {
        kind: "presence",
        shiftTypeId: shiftType.shiftTypeId,
        name: shiftType.name,
        backColor: shiftType.backColor,
        foreColor: shiftType.foreColor,
        canDirectAdd:
          shiftType.isAbsent ||
          (shiftType.startTimeDefault1 !== null &&
            shiftType.endTimeDefault1 !== null &&
            shiftType.startTimeDefault1 !== shiftType.endTimeDefault1) ||
          (shiftType.startTimeDefault2 !== null &&
            shiftType.endTimeDefault2 !== null &&
            shiftType.startTimeDefault2 !== shiftType.endTimeDefault2) ||
          (shiftType.startTimeDefault3 !== null &&
            shiftType.endTimeDefault3 !== null &&
            shiftType.startTimeDefault3 !== shiftType.endTimeDefault3),
        requiresTimeRangeInput:
          !shiftType.isAbsent &&
          shiftType.startTimeDefault1 === null &&
          shiftType.endTimeDefault1 === null &&
          shiftType.startTimeDefault2 === null &&
          shiftType.endTimeDefault2 === null &&
          shiftType.startTimeDefault3 === null &&
          shiftType.endTimeDefault3 === null,
      })
    }

    for (const shiftType of absenceShiftTypeQuery.data ?? []) {
      if (shiftType.shiftTypeId === undefined || !shiftType.name) {
        continue
      }
      const key = `absence-${shiftType.shiftTypeId}`
      if (merged.has(key)) {
        continue
      }
      merged.set(key, {
        kind: "absence",
        shiftTypeId: shiftType.shiftTypeId,
        name: shiftType.name,
        backColor: shiftType.backColor,
        foreColor: shiftType.foreColor,
        canDirectAdd: true,
        requiresTimeRangeInput: false,
      })
    }

    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [absenceShiftTypeQuery, shiftTypeQuery])

  useEffect(() => {
    if (!selectedQuickAddShift) {
      return
    }

    if (selectedQuickAddShift.action === "delete") {
      return
    }

    const stillAvailable = shiftTypes.some(
      (shiftType) =>
        shiftType.shiftTypeId === selectedQuickAddShift.shiftTypeId &&
        shiftType.kind === (selectedQuickAddShift.kind ?? "presence")
    )

    if (!stillAvailable) {
      setSelectedQuickAddShift(null)
    }
  }, [selectedQuickAddShift, setSelectedQuickAddShift, shiftTypes])

  const selectedShiftType = useMemo(
    () =>
      shiftTypes.find(
        (shiftType) =>
          shiftType.shiftTypeId === selectedQuickAddShift?.shiftTypeId &&
          shiftType.kind === (selectedQuickAddShift?.kind ?? "presence")
      ) ?? null,
    [
      selectedQuickAddShift?.kind,
      selectedQuickAddShift?.shiftTypeId,
      shiftTypes,
    ]
  )

  const selectedCanDirectAdd = Boolean(
    selectedQuickAddShift &&
    (selectedQuickAddShift.canDirectAdd ||
      (selectedShiftType?.requiresTimeRangeInput &&
        hasValidTimeRange(selectedQuickAddShift)))
  )
  const isDeleteMode = selectedQuickAddShift?.action === "delete"

  useEffect(() => {
    if (!selectedQuickAddShift) {
      return
    }

    if (selectedQuickAddShift.addMode === "direct" && !selectedCanDirectAdd) {
      setSelectedQuickAddShift({
        ...selectedQuickAddShift,
        addMode: "modal",
      })
    }
  }, [selectedCanDirectAdd, selectedQuickAddShift, setSelectedQuickAddShift])

  return (
    <>
      <Text size="10px" c="dimmed" mb={2}>
        {t("QuickAdd_Hint")}
      </Text>
      <Checkbox
        mt="xs"
        size="xs"
        label={t("QuickAdd_DirectAdd")}
        checked={!isDeleteMode && selectedQuickAddShift?.addMode !== "modal"}
        disabled={
          !selectedQuickAddShift || !selectedCanDirectAdd || isDeleteMode
        }
        onChange={(event) => {
          if (!selectedQuickAddShift) {
            return
          }

          setSelectedQuickAddShift({
            ...selectedQuickAddShift,
            action: "add",
            addMode: event.currentTarget.checked ? "direct" : "modal",
          })
        }}
      />
      {selectedQuickAddShift && selectedShiftType?.requiresTimeRangeInput ? (
        <Stack gap={4} mt={6}>
          <Text size="10px" c="dimmed">
            {t("QuickAdd_TimeRangeRequired")}
          </Text>
          <Group grow wrap="nowrap" align="end">
            <TimeInput
              label={t("From")}
              size="compact-xs"
              value={selectedQuickAddShift.startTime ?? ""}
              onChange={(event) => {
                const value = event.currentTarget.value || null
                setSelectedQuickAddShift({
                  ...selectedQuickAddShift,
                  startTime: value,
                })
              }}
            />
            <TimeInput
              label={t("Until")}
              size="compact-xs"
              value={selectedQuickAddShift.endTime ?? ""}
              onChange={(event) => {
                const value = event.currentTarget.value || null
                setSelectedQuickAddShift({
                  ...selectedQuickAddShift,
                  endTime: value,
                })
              }}
            />
          </Group>
        </Stack>
      ) : null}
      <Divider my={6} />
      {isLoading ? (
        <Text size="xs" c="dimmed">
          {t("QuickAdd_LoadingShifts")}
        </Text>
      ) : (
        <ScrollArea style={{ flex: 1, minHeight: 0 }}>
          <Stack gap={4}>
            <Button
              size="compact-xs"
              justify="flex-start"
              variant={"subtle"}
              color="red"
              styles={{
                root: {
                  border: isDeleteMode
                    ? "2px solid var(--mantine-color-red-6)"
                    : "1px solid transparent",
                },
              }}
              onClick={() =>
                setSelectedQuickAddShift(
                  isDeleteMode
                    ? null
                    : {
                        action: "delete",
                        kind: "presence",
                        canDirectAdd: true,
                        addMode: "direct",
                        startTime: null,
                        endTime: null,
                        shiftTypeId: -1,
                        name: "Delete mode",
                      }
                )
              }
            >
              {t("QuickAdd_DeleteMode")}
            </Button>

            {shiftTypes.length === 0 ? (
              <Text size="xs" c="dimmed">
                {t("QuickAdd_NoShifts")}
              </Text>
            ) : null}

            {shiftTypes.map((shiftType) => {
              const selected =
                !isDeleteMode &&
                selectedQuickAddShift?.shiftTypeId === shiftType.shiftTypeId &&
                (selectedQuickAddShift.kind ?? "presence") === shiftType.kind
              const backgroundColor = normalizeCssColor(shiftType.backColor)
              const textColor = getReadableTextColor(
                shiftType.backColor,
                shiftType.foreColor
              )
              const itemStyle = backgroundColor
                ? ({
                    "--button-bg": backgroundColor,
                    "--button-hover": backgroundColor,
                    "--button-color": textColor ?? "var(--mantine-color-white)",
                  } as React.CSSProperties)
                : undefined

              return (
                <Button
                  key={`${shiftType.kind}-${shiftType.shiftTypeId}`}
                  size="compact-xs"
                  justify="flex-start"
                  variant={backgroundColor ? "filled" : "subtle"}
                  style={itemStyle}
                  styles={{
                    root: {
                      border: selected
                        ? "2px solid var(--mantine-color-blue-6)"
                        : "1px solid transparent",
                    },
                  }}
                  onClick={() =>
                    setSelectedQuickAddShift(
                      selected
                        ? null
                        : {
                            kind: shiftType.kind,
                            action: "add",
                            canDirectAdd: shiftType.canDirectAdd,
                            addMode: shiftType.canDirectAdd
                              ? "direct"
                              : "modal",
                            startTime: null,
                            endTime: null,
                            shiftTypeId: shiftType.shiftTypeId,
                            name: shiftType.name,
                          }
                    )
                  }
                >
                  {shiftType.name}
                </Button>
              )
            })}
          </Stack>
        </ScrollArea>
      )}
    </>
  )
}
